import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import React, { useState, useEffect, useRef } from "react";
import Input from "../../../Components/Common/Input/Input";
import type {
  QAQuestion,
  QAAnswers,
  SubfieldAnswers,
} from "./preconConfig.types";

interface QAPanelProps {
  qaQuestions: QAQuestion[];
  qaAnswers: QAAnswers;
  onChange: (n: number, value: string | SubfieldAnswers) => void;
  qaCount: number;
  totalQA: number;
  fieldErrors?: Record<string, string>;
}

const QAPanel = ({
  qaQuestions,
  qaAnswers,
  onChange,
  qaCount,
  totalQA,
  fieldErrors,
}: QAPanelProps) => {
  const [subfieldErrors, setSubfieldErrors] = useState<Record<string, string>>(
    {},
  );
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Scroll to the first field that has an error
  useEffect(() => {
    if (!fieldErrors || Object.keys(fieldErrors).length === 0) return;
    const firstErrIdx = qaQuestions.findIndex((q) => fieldErrors[q.key]);
    if (firstErrIdx !== -1) {
      itemRefs.current[firstErrIdx + 1]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [fieldErrors, qaQuestions]);

  // Initialize subfield defaultValues into state whenever qaAnswers settles
  // (covers both first mount and after checklist data loads from API)
  useEffect(() => {
    qaQuestions.forEach((qa, idx) => {
      if (qa.type !== "subfields" || !qa.subfields) return;
      const n = idx + 1;
      const current = qaAnswers[n];
      const currentObj: SubfieldAnswers =
        typeof current === "object" ? (current as SubfieldAnswers) : {};
      const updates: SubfieldAnswers = {};
      qa.subfields.forEach((sf) => {
        if (sf.defaultValue && currentObj[sf.key] === undefined) {
          updates[sf.key] = sf.defaultValue;
        }
      });
      if (Object.keys(updates).length > 0) {
        onChange(n, { ...currentObj, ...updates });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qaQuestions]);

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-head-title">Questions</div>
        <div className="panel-head-count">
          {qaCount} / {totalQA} answered
        </div>
      </div>
      <div className="panel-body">
        {qaQuestions.map((qa, idx) => {
          const n = idx + 1;
          const value = qaAnswers[n] || "";

          return (
            <div
              key={n}
              className="qa-item"
              ref={(el) => {
                itemRefs.current[n] = el;
              }}
            >
              <div className="qa-num">Q{n}</div>
              <div className="qa-question">{qa.label}</div>
              {qa.hint && <div className="qa-hint">{qa.hint}</div>}
              {qa.type === "subfields" ? (
                <div className="qa-subfields">
                  {qa.subfields?.map((sf) => {
                    const currentValue =
                      typeof value === "object"
                        ? (value as SubfieldAnswers)[sf.key]
                        : undefined;
                    const sfValue =
                      currentValue !== undefined
                        ? currentValue
                        : (sf.defaultValue ?? "");
                    return (
                      <div
                        key={sf.key}
                        className={`qa-subfield${sf.fullWidth ? " full-width" : ""}`}
                      >
                        <label>{sf.label}</label>
                        <div className={sf.prefix ? "inputWithPrefix" : ""}>
                          {sf.prefix && (
                            <span className="prefixSymbol">{sf.prefix}</span>
                          )}
                          <Input
                            type="text"
                            className=""
                            placeholder={sf.placeholder}
                            value={sfValue}
                            inputMode={sf.numeric ? "numeric" : undefined}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              const errKey = `${n}_${sf.key}`;
                              let sanitized = e.target.value;
                              if (sf.numeric) {
                                sanitized = e.target.value.replace(/\D/g, "");
                                const max = sf.maxLength ?? 12;
                                if (sanitized.length > max) {
                                  sanitized = sanitized.slice(0, max);
                                  setSubfieldErrors((prev) => ({
                                    ...prev,
                                    [errKey]: `Maximum ${max} digits allowed`,
                                  }));
                                } else {
                                  setSubfieldErrors((prev) => {
                                    if (!prev[errKey]) return prev;
                                    const next = { ...prev };
                                    delete next[errKey];
                                    return next;
                                  });
                                }
                              }
                              const current =
                                typeof value === "object"
                                  ? (value as SubfieldAnswers)
                                  : {};
                              onChange(n, { ...current, [sf.key]: sanitized });
                            }}
                          />
                        </div>
                        {subfieldErrors[`${n}_${sf.key}`] && (
                          <span className="qa-subfield-error">
                            {subfieldErrors[`${n}_${sf.key}`]}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : qa.type === "textarea" ? (
                <TextArea
                  className={`qa-input${fieldErrors?.[qa.key] ? " error-input-red" : ""}`}
                  placeholder={qa.placeholder}
                  value={typeof value === "string" ? value : ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChange(n, e.target.value)
                  }
                  maxChars={CHAR_LIMIT.DESCRIPTION}
                />
              ) : qa.type === "date" ? (
                <Input
                  type="date"
                  className={`qa-date-input${fieldErrors?.[qa.key] ? " error-input-red" : ""}`}
                  value={typeof value === "string" ? value : ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(n, e.target.value)
                  }
                />
              ) : (
                <div className={qa.prefix ? "inputWithPrefix" : ""}>
                  {qa.prefix && (
                    <span className="prefixSymbol">{qa.prefix}</span>
                  )}
                  <Input
                    className={`qa-input${fieldErrors?.[qa.key] ? " error-input-red" : ""}`}
                    type="text"
                    inputMode={qa.numeric ? "numeric" : undefined}
                    placeholder={qa.placeholder}
                    maxLength={qa.maxLength}
                    value={typeof value === "string" ? value : ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let v = qa.numeric
                        ? e.target.value.replace(/\D/g, "")
                        : e.target.value;
                      if (qa.maxLength) v = v.slice(0, qa.maxLength);
                      onChange(n, v);
                    }}
                    label={undefined}
                    icon={""}
                  />
                </div>
              )}
              {fieldErrors?.[qa.key] && (
                <span className="qa-field-error">{fieldErrors[qa.key]}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="panel-footer">
        <div className="footer-content">
          If any question does not apply to this project, please enter{" "}
          <strong>N/A</strong> in the field.
        </div>
      </div>
    </div>
  );
};

export default QAPanel;
